import { flowRight, isUndefined } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  HEADER_SHOW_ON_CATEGORY_PAGE_PATH,
  IS_CATEGORY_HEADER_ENABLED_PATH,
  SECTION_CATEGORY,
} from '@wix/communities-blog-client-common/dist/src/constants/wix-params';
import { HEADER_SHOW_DEFAULT } from '@wix/communities-blog-client-common/dist/src/constants/header-sizes';
import { IS_CATEGORY_HEADER_ENABLED_DEFAULT } from '../../constants/category-header';
import { fetchCategory } from '../../actions/fetch-category';
import { fetchCategoryPosts } from '../../actions/fetch-category-posts';
import { incrementPostLikeCount } from '../../actions/increment-post-like-count';
import { pageOpened } from '../../actions/page-opened';
import { setIsCategoryLoaded } from '../../actions/set-is-category-loaded';
import { setIsLoaded } from '../../actions/set-is-loaded';
import { waitForAppSettings } from '../../actions/fetch-app-settings';
import { getConfig } from '../../services/config';
import { getRawOuterUrl } from '../../services/get-outer-url';
import getScrollOffset from '../../services/get-scroll-offset';
import outerHeight from '../../services/outer-height';
import scrollTo, { scrollToAppTop } from '../../services/scroll-to';
import { CATEGORY_PAGE } from '../../services/detect-route';
import { getPage, push } from '../../services/router-utils';
import { resolveCategorySlug } from '../../services/slug';
import { getCategorySchema } from '../../services/json-schema';
import { formatTitle } from '../../services/pagination';
import { getCategoryBySlug, getCategoriesMap } from '../../selectors/categories-selectors';
import { getIsAppLoaded } from '../../selectors/is-app-loaded-selectors';
import { getIsCategoryLoaded } from '../../selectors/is-loaded-selectors';
import { getIsCategoryLoading } from '../../selectors/is-loading-selectors';
import { getSortedPostsByCategoryId, getPostsByCategoryIdAndPage } from '../../selectors/post-selectors';
import { isOneApp } from '@wix/communities-blog-client-common/dist/src/selectors/search-params-selectors';
import { isSite } from '../../selectors/view-mode-selectors';
import { isExcludePostContentSupported } from '../../selectors/layout-selectors';
import withAppSettings from '../../hoc/with-app-settings';
import withAuth from '../../hoc/with-auth';
import withLayoutProps from '../../hoc/with-layout-props';
import withPaginationSettings, { getPaginationSettings } from '../../hoc/with-pagination-settings';
import withPermissions from '../../hoc/with-permissions';
import withTranslate from '../../hoc/with-translate';
import AccountSuspended from '../../components/account-suspended';
import AnimatedLoader from '../../components/animated-loader';
import AppLoaded from '../../components/app-loaded';
import CategoryDescription from '../../components/category-description';
import CategoryHeader from '../../components/category-header';
import CreatePostButtonMobile from '../../components/create-post-button-mobile';
import JsonSchema from '../../components/json-schema';
import { getCategoryUrl } from '../../services/get-category-url';
import NoPosts from '../no-posts';
import Page from '../../components/page';
import PageMetadata from '../../components/page-metadata';
import Pagination from '../pagination';
import PostList from '../../components/post-list';
import { EmptyLayout } from '../../components/layout';

const Layout = EmptyLayout;

export class CategoryPage extends Component {
  static fetchData = ({ store, params, query }) => {
    return store
      .dispatch(fetchCategory(resolveCategorySlug(params)))
      .then(category =>
        store.dispatch(
          waitForAppSettings(
            () =>
              fetchCategoryPosts({
                categoryId: category._id,
                page: getPage(params, query),
                excludeContent: isExcludePostContentSupported(store.getState()),
              }),
            true,
          ),
        ),
      )
      .then(() => {
        const { showPagination, isValidPage } = getPaginationSettings(SECTION_CATEGORY, store.getState(), { params });
        if (showPagination && !isValidPage) {
          const error = { status: 404 };
          throw error;
        }
      });
  };

  state = {};

  constructor(props) {
    super(props);
    this.handleLikeClick = this.handleLikeClick.bind(this);
    this.state = {
      isAnimated: !props.allPosts.length,
    };
  }

  componentDidMount() {
    const { category, page, isAppLoaded } = this.props;
    this.props.pageOpened({
      page: CATEGORY_PAGE,
      category,
    });

    if (isAppLoaded && page === 1) {
      this.fetchPosts(category._id);
    }
  }

  componentWillUnmount() {
    const { category } = this.props;
    this.props.setIsLoaded('posts', category._id, false);
  }

  componentWillReceiveProps({ categorySlug, category, newPostId }) {
    const currentCategorySlug = this.props.categorySlug;

    if (currentCategorySlug !== categorySlug) {
      this.fetchPosts(category._id);
    }

    if (newPostId && newPostId !== this.props.newPostId) {
      const postElement = document.getElementById(newPostId);
      if (postElement) {
        const rect = postElement.getBoundingClientRect();
        const height = outerHeight(postElement);
        if (rect.top < 0) {
          scrollTo(getScrollOffset() + height);
        }
      }
    }
  }

  shouldComponentUpdate(nextProps) {
    return !isUndefined(nextProps.entityCount);
  }

  componentDidUpdate({ isAuthenticated, isBlocked, pageSize, excludeContent }) {
    const { category } = this.props;
    if (
      this.props.isAuthenticated !== isAuthenticated ||
      this.props.isBlocked !== isBlocked ||
      this.props.pageSize !== pageSize ||
      (excludeContent && !this.props.excludeContent)
    ) {
      this.fetchPosts(category._id);
    }
  }

  fetchPosts(categoryId, page = this.props.page) {
    if (this.props.isBlocked) {
      return;
    }

    this.props.fetchPosts({ categoryId, page, excludeContent: this.props.excludeContent });
  }

  handleLikeClick(postId) {
    this.props.incrementPostLikeCount(postId);
  }

  changePage = page => {
    this.fetchPosts(this.props.category._id, page);
    this.props.push({ pathname: this.createPagePath(page) });
    if (this.props.showPagination) {
      scrollToAppTop();
    }
  };

  createPagePath = page => {
    const { categorySlug, categoryPath } = this.props;

    return page > 1 ? `/${categoryPath}/${categorySlug}/page/${page}/` : `/${categoryPath}/${categorySlug}/`;
  };
  createPageUrl = page => getRawOuterUrl(this.createPagePath(page)).toString();

  loadMore = page => this.fetchPosts(this.props.category._id, page);

  renderPosts() {
    const {
      isBlocked,
      currentPagePosts,
      allPosts,
      entityCount,
      category,
      isCategoryLoading,
      isCategoryLoaded,
      showCreatePostAction,
      page,
      layoutType,
      isOneApp,
      showPagination,
    } = this.props;
    const hasPosts = allPosts.length;

    if (isBlocked) {
      return <AccountSuspended />;
    }

    if (!hasPosts && isCategoryLoaded) {
      return <NoPosts category={category} />;
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        changePage={this.changePage}
        loadMore={showPagination ? undefined : this.loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        onLikeClick={this.handleLikeClick}
        isLoading={isCategoryLoading}
        category={category}
        isLoaded={isCategoryLoaded}
        showCreatePostAction={showCreatePostAction && !isOneApp}
      />
    );
  }

  renderPagination() {
    const { entityCount, pageSize, page } = this.props;
    return (
      <Pagination
        page={page}
        pageSize={pageSize}
        entityCount={entityCount}
        onChange={this.changePage}
        createPageUrl={this.createPageUrl}
      />
    );
  }

  render() {
    const {
      category,
      isCategoryLoading,
      isCategoryLoaded,
      allPosts,
      showHeader,
      showCreatePostAction,
      isOneApp,
      canonicalUrl,
      isCategoryHeaderEnabled,
      showPagination,
      jsonSchema,
      page,
      lastPage,
      useCategoryMenuLabelForMetadataTitle,
      t,
    } = this.props;

    const title = useCategoryMenuLabelForMetadataTitle ? category.menuLabel : category.label;

    const metadata = {
      title: showPagination ? formatTitle({ title, page, lastPage, t }) : title,
      description: category.description,
      image: category.cover,
      canonicalUrl: showPagination ? this.createPageUrl(page) : canonicalUrl,
      prevUrl: showPagination && page > 1 ? this.createPageUrl(page - 1) : undefined,
      nextUrl: showPagination && page < lastPage ? this.createPageUrl(page + 1) : undefined,
    };

    return (
      <Page noSpacing={!isOneApp}>
        <Layout>
          <PageMetadata key={lastPage} {...metadata} />
          {jsonSchema && <JsonSchema content={jsonSchema} />}
          {showHeader && <CategoryDescription category={category} headerSize={200} />}
          {isCategoryHeaderEnabled && <CategoryHeader category={category} />}
          {isOneApp && showCreatePostAction ? <CreatePostButtonMobile /> : null}
          <AnimatedLoader
            isLoading={isCategoryLoading && !isCategoryLoaded && !allPosts.length}
            isAnimated={this.state.isAnimated}
          >
            {this.renderPosts()}
          </AnimatedLoader>
          {showPagination && this.renderPagination()}
          {isCategoryLoaded && <AppLoaded />}
        </Layout>
      </Page>
    );
  }
}

CategoryPage.propTypes = {
  categorySlug: PropTypes.string.isRequired,
  categoryPath: PropTypes.string.isRequired,
  fetchPosts: PropTypes.func.isRequired,
  pageOpened: PropTypes.func.isRequired,
  incrementPostLikeCount: PropTypes.func.isRequired,
  categoryId: PropTypes.string, // no id when creating new category in editor
  location: PropTypes.object.isRequired,
  setIsCategoryLoaded: PropTypes.func.isRequired,
  setIsLoaded: PropTypes.func.isRequired,
  push: PropTypes.func,
  allPosts: PropTypes.array,
  currentPagePosts: PropTypes.array,
  category: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  isBlocked: PropTypes.bool,
  params: PropTypes.object,
  t: PropTypes.func,
  newPostId: PropTypes.string,
  isCategoryLoading: PropTypes.bool,
  isCategoryLoaded: PropTypes.bool,
  showCreatePostAction: PropTypes.bool.isRequired,
  layoutType: PropTypes.number.isRequired,
  showHeader: PropTypes.bool,
  isAppLoaded: PropTypes.bool,
  isOneApp: PropTypes.bool.isRequired,
  canonicalUrl: PropTypes.string,
  isCategoryHeaderEnabled: PropTypes.bool,
  excludeContent: PropTypes.bool,
  pageSize: PropTypes.number.isRequired,
  page: PropTypes.number,
  lastPage: PropTypes.number,
  entityCount: PropTypes.number,
  showPagination: PropTypes.bool,
  jsonSchema: PropTypes.object,
  useCategoryMenuLabelForMetadataTitle: PropTypes.bool,
};

CategoryPage.defaultProps = {
  showHeader: HEADER_SHOW_DEFAULT,
  isCategoryHeaderEnabled: IS_CATEGORY_HEADER_ENABLED_DEFAULT,
};

const mapStateToProps = (state, ownProps) => {
  const categorySlug = resolveCategorySlug(ownProps.params);
  const category = getCategoryBySlug(state, categorySlug) || {};
  const categoryId = category._id;
  const currentPagePosts = categoryId ? getPostsByCategoryIdAndPage(state, categoryId, ownProps.page) : [];
  const { sectionUrl, publisher, categoryPath, useCategoryMenuLabelForMetadataTitle } = getConfig();

  return {
    categorySlug,
    categoryPath,
    category,
    allPosts: categoryId ? getSortedPostsByCategoryId(state, categoryId) : [],
    currentPagePosts,
    newPostId: state.newPostMessage,
    categoryId,
    isCategoryLoading: getIsCategoryLoading(state, categoryId),
    isCategoryLoaded: getIsCategoryLoaded(state, categoryId),
    showCreatePostAction: isSite(state) && ownProps.canSee('create', 'post'),
    excludeContent: isExcludePostContentSupported(state),
    isAppLoaded: getIsAppLoaded(state),
    isOneApp: isOneApp(state),
    canonicalUrl: getCategoryUrl(sectionUrl, categoryPath, categorySlug),
    jsonSchema: publisher ? getCategorySchema(state, category, currentPagePosts, getCategoriesMap(state)) : undefined,
    useCategoryMenuLabelForMetadataTitle,
  };
};

const mapDispatchToProps = {
  fetchPosts: fetchCategoryPosts,
  setIsCategoryLoaded,
  setIsLoaded,
  incrementPostLikeCount,
  pageOpened,
  push,
};

export default flowRight(
  withPermissions,
  withPaginationSettings(SECTION_CATEGORY),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withLayoutProps(),
  withAuth,
  withAppSettings({
    showHeader: HEADER_SHOW_ON_CATEGORY_PAGE_PATH,
    isCategoryHeaderEnabled: IS_CATEGORY_HEADER_ENABLED_PATH,
  }),
  withTranslate,
)(CategoryPage);
